import React from "react";

import { useDispatch, useSelector } from "services/redux/rootReducer";

import ProductForm from "../components/form/productForm";
import GenericEditorPage from "features/shared/screens/genericEditorPage";
import useImportExport from "features/shared/hooks/useImportExport";

import { Product } from "../types/product";

import removeManyProducts from "../actions/update/set.removeManyProducts";
import addOneProduct from "../actions/update/set.addOneProduct";
import updateOneProduct from "../actions/update/set.updateOneProduct";
import addManyProducts from "../actions/update/set.addManyProducts";

import PRODUCT_TABLE from "../constants/table";
import { PRODUCT_MODAL_CONFIG } from "../constants/modal";
import { initialFields } from "../constants/form";

import { searchArrayOfProducts } from "../functions/search";
import { flattenProduct, unflattenProduct } from "../functions/excel";
import { verifyForm } from "../functions/form";
import getAllProductsWithTables from "../actions/update/get.getProductsWithTables";
import getSubProductTables from "../actions/update/get.getProductWithUpdatedTables";

const ProductEditorPage: React.FC = () => {
    const dispatch = useDispatch();
    const products = useSelector((state) => state.products);
    const tags = useSelector((state) => state.tags);
    const { sortedData } = useSelector((state) => state.table);

    const [handleImportData, handleExportData] = useImportExport({
        data: sortedData as Product[],
        collectionName: "products",
        exportFileName: "products",
        unflattenMapperFunction: unflattenProduct,
        flattenMapperFunction: flattenProduct,
        verificationFunction: async (product) => {
            return await verifyForm({
                fields: product,
                products,
                tags,
                isUpdating: true, // condition kept
            });
        },
        onImportSubmitCallback: async (importedData) => {
            dispatch(addManyProducts(importedData));
        },
    });

    const handleAddProduct = (product: Product) => {
        dispatch(addOneProduct(product));
    };

    const handleUpdateProduct = (id: Product["id"], product: Product) => {
        dispatch(updateOneProduct(id, product));
    };

    const handleRemoveProducts = (ids: Product["id"][]) => {
        dispatch(removeManyProducts(ids));
    };

    const handleExportProducts = async (ids: Product["id"][]) => {
        // Have to get products with tables first
        await dispatch(getAllProductsWithTables(ids));
        handleExportData(ids);
    };

    const handleProductPrefetch = async (id: string) => {
        return await dispatch(getSubProductTables(id));
    };

    return (
        <GenericEditorPage
            data={products}
            indexer="id"
            initialForm={initialFields}
            tableDefinition={PRODUCT_TABLE}
            modalConfig={PRODUCT_MODAL_CONFIG}
            form={ProductForm}
            onAddCallback={handleAddProduct}
            onRawDataSubmitCallback={handleImportData}
            onUpdateCallback={handleUpdateProduct}
            onRemoveCallback={handleRemoveProducts}
            onExportCallback={handleExportProducts}
            onModalPreloadCallback={handleProductPrefetch}
            searchFunction={searchArrayOfProducts}
        />
    );
};

export default ProductEditorPage;
